// public/js/features/files/settings-doc-types.js
// Settings → Document types. The tenant-facing half of the file store's type
// registry (docs/asset-files.md §2): platform types can be renamed or switched
// off per tenant, and a tenant can add its own types. Deliberately mirrors the
// Asset Fields tab — same mental model, same coupling to the global tenant
// scope (the sidebar selector is the only tenant choice in the app).
import { can, getCurrentUser } from '../../modules/rbac.js';
import { scopeTenantParam } from '../../modules/tenant-scope.js';
import { scopePromptHtml } from '../../ui/tenant-scope-widget.js';
import { getMemberships } from '../../modules/tenant-scope.js';
import { escapeHtml } from '../../utils/escape.js';

// Per-user, per-browser preference — nothing worth a round trip to the server.
const SHOW_KEYS_STORAGE = 'superb.docTypes.showKeys';
const readShowKeys = () => {
  try { return localStorage.getItem(SHOW_KEYS_STORAGE) === '1'; } catch { return false; }
};
const writeShowKeys = (on) => {
  try { localStorage.setItem(SHOW_KEYS_STORAGE, on ? '1' : '0'); } catch { /* private mode */ }
};

async function api(path, opts) {
  const res = await window.authenticatedFetch(path, opts);
  if (!res.ok) {
    let msg = `Request failed (${res.status})`;
    try { msg = (await res.json()).error || msg; } catch {}
    throw new Error(msg);
  }
  return res.json();
}

export async function renderDocTypeSettings(container) {
  const groups = getCurrentUser().groups || [];
  if (!can(groups, 'manageDocTypes')) {
    container.innerHTML = `<div class="alert alert-danger">Access denied.</div>`;
    return;
  }

  // Hard-coupled to the global scope, like every other settings tab.
  const tenantId = scopeTenantParam();
  if (!tenantId || tenantId === 'unassigned') {
    container.innerHTML = scopePromptHtml('document types');
    return;
  }
  const tenantName = getMemberships().find(t => t.tenantId === tenantId)?.name || tenantId;

  container.innerHTML = `<div class="text-muted py-3"><span class="spinner-border spinner-border-sm me-2"></span>Loading…</div>`;

  let types;
  try {
    types = (await api(`api/v1/doc-types?tenant=${encodeURIComponent(tenantId)}`)).docTypes || [];
  } catch (e) {
    container.innerHTML = `<div class="alert alert-danger">Failed to load document types: ${escapeHtml(e.message)}</div>`;
    return;
  }

  const reload = () => renderDocTypeSettings(container);
  const showKeys = readShowKeys();
  const row = (t) => `
    <div class="list-group-item d-flex align-items-center gap-3 flex-wrap" data-type-id="${escapeHtml(t.docTypeId)}">
      <div class="form-check form-switch m-0" title="Show this type when uploading">
        <input class="form-check-input dt-enabled" type="checkbox" ${t.enabled ? 'checked' : ''}>
      </div>
      <input class="form-control form-control-sm dt-label" style="max-width:280px;" value="${escapeHtml(t.label)}"
             placeholder="Type name">
      ${t.custom
        ? `<span class="badge badge-soft-blue">Tenant type</span>`
        : `<span class="badge badge-soft-gray">Standard</span>`}
      <code class="dt-key small text-muted${showKeys ? '' : ' d-none'}"
            title="Fixed key - renaming the type never changes it">${escapeHtml(t.docTypeId)}</code>
      <div class="ms-auto d-flex gap-2">
        <button class="btn btn-sm btn-outline-secondary dt-save"><i class="fa-solid fa-check me-1"></i>Save</button>
        ${t.custom ? `<button class="btn btn-sm btn-outline-danger dt-delete" title="Delete this tenant type"><i class="fa-solid fa-trash"></i></button>` : ''}
      </div>
    </div>`;

  container.innerHTML = `
    <div class="card shadow-sm mb-4 border-primary">
      <div class="card-header bg-primary text-white d-flex align-items-center gap-2">
        <i class="fa-solid fa-folder-tree"></i>
        <span class="fw-semibold">Document types for <span class="tenant-name">${escapeHtml(tenantName)}</span></span>
        <div class="form-check form-switch m-0 ms-auto" title="Show the fixed key of each type">
          <input class="form-check-input" type="checkbox" role="switch" id="dt-show-keys" ${showKeys ? 'checked' : ''}>
          <label class="form-check-label small" for="dt-show-keys">Show keys</label>
        </div>
        <span class="badge bg-light text-primary">applies to this tenant only</span>
      </div>
      <div class="card-body">
        <p class="text-muted small mb-3">
          Every file uploaded to an asset is filed under one of these types. Switch a standard type off to hide it
          from the upload dialog, rename it to match your own wording, or add types of your own.
          <br><br>Every type also has a <b>fixed key</b> that renaming never changes — that is what reports, exports
          and the API refer to. Switch on <b>Show keys</b> to see them.
          <br><br><b>Aviation:</b> an MRO renames “Release form” to “EASA Form 1” and adds a tenant type “Borescope report”.
        </p>
        <div class="list-group mb-3" id="dt-list">${types.map(row).join('')}</div>
        <div class="d-flex gap-2 align-items-center">
          <input id="dt-new-label" class="form-control form-control-sm" style="max-width:280px;" placeholder="New type name…">
          <button id="dt-add" class="btn btn-sm btn-success"><i class="fa-solid fa-plus me-1"></i>Add type</button>
        </div>
      </div>
    </div>`;

  const save = async (el) => {
    const id = el.closest('[data-type-id]').dataset.typeId;
    const label = el.closest('[data-type-id]').querySelector('.dt-label').value.trim();
    const enabled = el.closest('[data-type-id]').querySelector('.dt-enabled').checked;
    try {
      await api('api/v1/doc-types', {
        method: 'PATCH', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tenantId, docTypeId: id, label, enabled }),
      });
      window.showToast?.('Saved', 'success');
    } catch (e) { window.showToast?.(e.message || 'Save failed', 'danger'); }
  };

  // Keys are hidden by default (they mean nothing to most users) but matter as
  // soon as someone builds an export or calls the API, so the choice sticks.
  container.querySelector('#dt-show-keys')?.addEventListener('change', (e) => {
    const on = e.target.checked;
    writeShowKeys(on);
    container.querySelectorAll('.dt-key').forEach(el => el.classList.toggle('d-none', !on));
  });

  container.querySelectorAll('.dt-save').forEach(b => b.addEventListener('click', () => save(b)));
  // A toggle is a decision in itself — persist it immediately, no extra click.
  container.querySelectorAll('.dt-enabled').forEach(cb => cb.addEventListener('change', () => save(cb)));

  container.querySelectorAll('.dt-delete').forEach(b => b.addEventListener('click', async () => {
    const id = b.closest('[data-type-id]').dataset.typeId;
    if (!confirm('Delete this document type? Files already filed under it keep their reference.')) return;
    try {
      await api('api/v1/doc-types', {
        method: 'DELETE', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tenantId, docTypeId: id }),
      });
      window.showToast?.('Type deleted', 'success');
      reload();
    } catch (e) { window.showToast?.(e.message || 'Delete failed', 'danger'); }
  }));

  container.querySelector('#dt-add')?.addEventListener('click', async () => {
    const input = container.querySelector('#dt-new-label');
    const label = input.value.trim();
    if (!label) { window.showToast?.('Enter a name for the new type', 'warning'); return; }
    try {
      await api('api/v1/doc-types', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tenantId, label, enabled: true }),
      });
      window.showToast?.('Type added', 'success');
      reload();
    } catch (e) { window.showToast?.(e.message || 'Could not add the type', 'danger'); }
  });
}
